// pages/HomePage.tsx - Potkurikorjaus homepage
import React from 'react';
import { Anchor, Clock, Shield, Star, MessageSquare, Phone, ArrowRight, Search, FileText, Wrench, CheckCircle, Truck } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { getPath } from '../config/routes';

const HomePage: React.FC = () => {
  const { language, t } = useLanguage();

  const processSteps = [
    {
      icon: <Phone className="w-6 h-6" />,
      titleKey: 'home.process.step1.title',
      descKey: 'home.process.step1.desc'
    },
    {
      icon: <Truck className="w-6 h-6" />,
      titleKey: 'home.process.step2.title',
      descKey: 'home.process.step2.desc'
    },
    {
      icon: <Search className="w-6 h-6" />,
      titleKey: 'home.process.step3.title',
      descKey: 'home.process.step3.desc'
    },
    {
      icon: <FileText className="w-6 h-6" />,
      titleKey: 'home.process.step4.title',
      descKey: 'home.process.step4.desc'
    },
    {
      icon: <Wrench className="w-6 h-6" />,
      titleKey: 'home.process.step5.title',
      descKey: 'home.process.step5.desc'
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      titleKey: 'home.process.step6.title',
      descKey: 'home.process.step6.desc'
    },
    {
      icon: <Truck className="w-6 h-6" />,
      titleKey: 'home.process.step7.title',
      descKey: 'home.process.step7.desc'
    }
  ];

  const features = [
    { icon: <Clock className="w-8 h-8" />, titleKey: 'home.features.quality.title', descKey: 'home.features.quality.desc' },
    { icon: <Shield className="w-8 h-8" />, titleKey: 'home.features.warranty.title', descKey: 'home.features.warranty.desc' },
    { icon: <Star className="w-8 h-8" />, titleKey: 'home.features.satisfaction.title', descKey: 'home.features.satisfaction.desc' },
    { icon: <Anchor className="w-8 h-8" />, titleKey: 'home.features.experience.title', descKey: 'home.features.experience.desc' }
  ];

  const services = [
    { titleKey: 'home.services.repair.title', descKey: 'home.services.repair.desc', priceKey: 'home.services.repair.price' },
    { titleKey: 'home.services.maintenance.title', descKey: 'home.services.maintenance.desc', priceKey: 'home.services.maintenance.price' },
    { titleKey: 'home.services.parts.title', descKey: 'home.services.parts.desc', priceKey: 'home.services.parts.price', link: '/yhteystiedot' }
  ];

  return (
    <div className="pt-20 min-h-screen">
      {/* Hero */}
      <section className="relative min-h-[600px] bg-gradient-to-br from-cyan-900 via-blue-900 to-teal-900 flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <div className="inline-block mb-4">
              <div className="px-4 py-2 bg-cyan-500/20 backdrop-blur-sm rounded-full border border-cyan-400/30">
                <span className="text-cyan-200 text-sm font-medium">{t('home.hero.badge')}</span>
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              {t('home.hero.title')}<br />
              <span className="text-cyan-300">{t('home.hero.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto">
              {t('home.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`${getPath('contact', language)}#lomake`} className="bg-cyan-500 text-white px-8 py-4 rounded-full font-semibold hover:bg-cyan-400 transition-all inline-flex items-center justify-center gap-2">
                <MessageSquare className="w-5 h-5" />
                {t('home.hero.cta.message')}
              </a>
              <a href="tel:+358400416466" className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-cyan-900 transition-all inline-flex items-center justify-center gap-2">
                <Phone className="w-5 h-5" />
                {t('home.hero.cta.call')}
              </a>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white via-white/50 to-transparent pointer-events-none" />
      </section>

      {/* Quick Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            {features.map((feature, idx) => (
              <div key={idx} className="text-center p-6 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl hover:shadow-lg transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl text-white mb-4">
                  {feature.icon}
                </div>
                <h3 className="font-bold text-lg mb-2">{t(feature.titleKey)}</h3>
                <p className="text-gray-600">{t(feature.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">{t('home.services.title')}</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {services.map((service, idx) => (
              <div key={idx} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow">
                <h3 className="text-xl font-bold mb-3">{t(service.titleKey)}</h3>
                <p className="text-gray-600 mb-4">{t(service.descKey)}</p>
                {service.link ? (
                  <a href={service.link} className="text-2xl font-bold text-cyan-600 hover:text-cyan-700 inline-block">
                    {t(service.priceKey)}
                  </a>
                ) : (
                  <p className="text-2xl font-bold text-cyan-600">{t(service.priceKey)}</p>
                )}
              </div>
            ))}
          </div>
          <div className="text-center">
            <a href={getPath('services', language)} className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-8 py-4 rounded-full font-semibold hover:shadow-xl transition-all">
              {t('home.services.viewAll')}
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Korjausprosessi */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('home.process.title')}</h2>
            <p className="text-xl text-gray-600">{t('home.process.subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.slice(0, 4).map((step, idx) => (
              <div key={idx} className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center text-white mb-4">
                  {step.icon}
                </div>
                <h3 className="font-bold text-lg mb-2">{t(step.titleKey)}</h3>
                <p className="text-gray-700 text-sm">{t(step.descKey)}</p>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-6">
            {processSteps.slice(4, 7).map((step, idx) => (
              <div key={idx} className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center text-white mb-4">
                  {step.icon}
                </div>
                <h3 className="font-bold text-lg mb-2">{t(step.titleKey)}</h3>
                <p className="text-gray-700 text-sm">{t(step.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-600 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('home.cta.title')}
          </h2>
          <p className="text-xl text-cyan-100 mb-8">
            {t('home.cta.subtitle')}
          </p>
          <div className="flex justify-center">
            <a href={getPath('contact', language)} className="bg-white text-cyan-600 px-8 py-4 rounded-full font-semibold hover:shadow-xl transition-all">
              {t('home.cta.button')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
